import { useState, useEffect, useCallback } from 'react'
import { Plus, Play, Pause, GitBranch, Workflow, Pencil, History, Trash2, MoreVertical } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { useToast } from '@/components/ui/use-toast'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import { PermissionGate } from '@/components/auth/permission-gate'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { workflowService, type WorkflowOData, type WorkflowStats } from '@/lib/api/services/workflows'

export default function Workflows() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { confirm, ConfirmDialogComponent } = useConfirmDialog()
  const [workflows, setWorkflows] = useState<WorkflowOData[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<WorkflowStats | null>(null)

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = getODataParams()
      const response = await workflowService.getWorkflows(params)
      setWorkflows(response.value || [])
      setTotalCount(response['@odata.count'] || response.value?.length || 0)
    } catch (_error) {
      setError('Failed to load workflows')
      toast({
        title: 'Error',
        description: 'Failed to load workflows',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast])

  const loadStats = useCallback(async () => {
    try {
      const data = await workflowService.getStats()
      setStats(data)
    } catch (_error) {
      console.error('Failed to load workflow stats:', _error)
    }
  }, [])

  useEffect(() => {
    loadWorkflows()
    loadStats()
  }, [loadWorkflows, loadStats])

  const handleToggleStatus = async (workflowId: string, currentStatus: string) => {
    try {
      if (currentStatus === 'active') {
        await workflowService.deactivateWorkflow(workflowId)
      } else {
        await workflowService.activateWorkflow(workflowId)
      }
      toast({
        title: 'Success',
        description: `Workflow ${currentStatus === 'active' ? 'deactivated' : 'activated'} successfully`,
      })
      loadWorkflows()
      loadStats()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update workflow status',
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async (workflowId: string) => {
    const confirmed = await confirm({
      title: 'Delete Workflow',
      description: 'Are you sure you want to delete this workflow? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'destructive',
    })
    if (!confirmed) return

    try {
      await workflowService.deleteWorkflow(workflowId)
      toast({
        title: 'Success',
        description: 'Workflow deleted successfully',
      })
      loadWorkflows()
      loadStats()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete workflow',
        variant: 'destructive',
      })
    }
  }

  const handleRunWorkflow = async (workflowId: string) => {
    try {
      await workflowService.runWorkflow(workflowId)
      toast({
        title: 'Success',
        description: 'Workflow started successfully',
      })
      loadWorkflows()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to run workflow',
        variant: 'destructive',
      })
    }
  }

  const columns: ColumnDef<WorkflowOData>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (workflow) => (
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600">
            <GitBranch className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{workflow.name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
              {workflow.description || '-'}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Draft', value: 'draft' },
      ],
      cell: (workflow) => (
        <Badge
          variant={
            workflow.status === 'active'
              ? 'success'
              : workflow.status === 'draft'
              ? 'warning'
              : 'secondary'
          }
        >
          {workflow.status}
        </Badge>
      ),
    },
    {
      id: 'steps',
      header: 'Steps',
      accessor: 'steps',
      sortable: true,
      cell: (workflow) => <span>{workflow.steps}</span>,
    },
    {
      id: 'totalRuns',
      header: 'Runs',
      accessor: 'totalRuns',
      sortable: true,
      cell: (workflow) => <span>{workflow.totalRuns}</span>,
    },
    {
      id: 'lastRunAt',
      header: 'Last Run',
      accessor: 'lastRunAt',
      sortable: true,
      cell: (workflow) => (
        <span className="text-gray-500 dark:text-gray-400">
          {workflow.lastRunAt
            ? new Date(workflow.lastRunAt).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      width: '100px',
      cell: (workflow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            <DropdownMenuItem
              onClick={() => navigate(`/workflows/builder?id=${workflow.id}`)}
              className="rounded-lg gap-2"
            >
              <Pencil className="h-4 w-4" />
              Edit Workflow
            </DropdownMenuItem>
            {workflow.status === 'active' && (
              <DropdownMenuItem onClick={() => handleRunWorkflow(workflow.id)} className="rounded-lg gap-2">
                <Play className="h-4 w-4" />
                Run Now
              </DropdownMenuItem>
            )}
            {workflow.status !== 'draft' && (
              <DropdownMenuItem
                onClick={() => handleToggleStatus(workflow.id, workflow.status)}
                className="rounded-lg gap-2"
              >
                {workflow.status === 'active' ? (
                  <>
                    <Pause className="h-4 w-4" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Activate
                  </>
                )}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => navigate(`/workflows/${workflow.id}/history`)}
              className="rounded-lg gap-2"
            >
              <History className="h-4 w-4" />
              View History
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <PermissionGate permissions={['workflows.delete']}>
              <DropdownMenuItem
                className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20 gap-2"
                onClick={() => handleDelete(workflow.id)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {ConfirmDialogComponent}
      <PageHeader
        title="Workflows"
        description="Automate your document processes"
        actions={
          <PermissionGate permissions={['workflows.create']}>
            <Link
              to="/workflows/builder"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors shadow-lg shadow-gray-900/20 dark:shadow-white/20"
            >
              <Plus className="h-4 w-4" />
              Create Workflow
            </Link>
          </PermissionGate>
        }
      />

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: 'Total Workflows', value: stats?.totalWorkflows || 0, subtext: `${stats?.activeWorkflows || 0} active`, color: 'from-cyan-400 to-blue-600' },
          { label: 'Total Runs', value: stats?.totalRuns || 0, subtext: 'All time executions', color: 'from-emerald-400 to-green-600' },
          { label: 'Avg. Steps', value: stats?.avgStepsPerWorkflow?.toFixed(1) || 0, subtext: 'Per workflow', color: 'from-violet-400 to-purple-600' },
          { label: 'Success Rate', value: `${stats?.avgSuccessRate?.toFixed(1) || 0}%`, subtext: 'Last 30 days', color: 'from-amber-400 to-orange-600' },
        ].map((stat) => (
          <FluentCard key={stat.label} className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
              <Workflow className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            </div>
          </FluentCard>
        ))}
      </div>

      <FluentCard className="overflow-hidden">
        <DataTable
            columns={columns}
            data={workflows}
            totalCount={totalCount}
            loading={loading}
            error={error}
            onRetry={loadWorkflows}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={setSort}
            filters={filters}
            onFilterChange={setFilters}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search workflows..."
            rowKey="id"
            emptyMessage="No workflows found. Create your first workflow to get started."
          />
      </FluentCard>
    </div>
  )
}
